import { isPiiPropertyKey, type AnalyticsRecord } from "./privacy";

/**
 * App property keys allowed to carry a string value: game ids and enums from the
 * tracking plan (§8 "only anonId + game ids + numbers/enums"). Anything outside
 * this list is dropped, so a future `track("x", { comment: userInput })` cannot
 * leak silently.
 */
export const ALLOWED_STRING_PROPS: readonly string[] = [
  "achievementId",
  "clientId",
  "contractId",
  "grade",
  "lang",
  "lastScreen",
  "method",
  "minigameType",
  "platform",
  "screen",
  "sdk",
  "sessionId",
  "skillId",
  "toolId",
  "type",
];

/** Ids and enums are slugs; free text (spaces, punctuation, long values) is not. */
const SAFE_STRING = /^[A-Za-z0-9_.:@-]{1,64}$/;

function isAllowedValue(key: string, value: unknown): boolean {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value === "boolean") {
    return true;
  }
  if (typeof value === "string") {
    return ALLOWED_STRING_PROPS.includes(key) && SAFE_STRING.test(value);
  }
  return false;
}

/**
 * Fail-closed scrub for analytics properties. App keys pass only when the
 * allowlist above admits them; the SDK's own `$`-namespace (`$lib`,
 * `$insert_id`, `$session_id` and friends, which the transport needs) is kept
 * except for the personal-data keys matched by {@link isPiiPropertyKey}.
 * Returns a new record; the input is untouched.
 */
export function scrubAnalyticsProps<T extends AnalyticsRecord>(props: T): Partial<T> {
  const kept = Object.entries(props).filter(([key, value]) =>
    key.startsWith("$") ? !isPiiPropertyKey(key) : isAllowedValue(key, value),
  );
  return Object.fromEntries(kept) as Partial<T>;
}
